import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import { toast } from "sonner";
import { Plus, Filter, X, AlertTriangle, Menu, Send, FileEdit, Lock, MapPin, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { createMarkerIcon } from "../lib/markers";
import * as api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import AuthModal from "../components/AuthModal";
import AppHeader from "../components/AppHeader";
import InAppMessageModal from "../components/InAppMessageModal";
import OnboardingModal from "../components/OnboardingModal";
import AccountSheet from "../components/AccountSheet";
import CruisingSheet from "../components/CruisingSheet";
import { PhotoGrid, TagList } from "../components/SpotComponents";

function MapClickHandler({ onMapClick, isAddingSpot }) {
  useMapEvents({
    click: (e) => {
      if (isAddingSpot) onMapClick(e.latlng);
    },
  });
  return null;
}

function MapCenter({ center, focusTarget }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  useEffect(() => {
    if (focusTarget) map.setView([focusTarget.lat, focusTarget.lng], 18);
  }, [focusTarget, map]);
  return null;
}

const TRACE_LABELS = {
  passed: "J'y suis passé",
  regular: "Régulier",
  want_to_try: "Envie"
};

const CATEGORY_BADGE_CLASS = {
  facile:     "border-emerald-500/30 bg-emerald-500/15 text-emerald-200",
  audacieux:  "border-amber-500/30 bg-amber-500/15 text-amber-200",
  temporaire: "border-red-500/30 bg-red-500/15 text-red-200",
};

const STATUS_LABELS = {
  draft: "Brouillon",
  visible: "Publié",
  masked: "Masqué"
};

const DESCRIPTION_MAX = 200;

const PREDEFINED_TAGS = [
  { label: "Environnement", tags: ["Nature", "Bord de l'eau", "Urbain"] },
  { label: "Ambiance",      tags: ["Discret", "Fréquenté", "Nocturne", "Diurne"] },
  { label: "Profil",        tags: ["Hommes", "Mixte", "Naturiste"] },
];

const deduplicateSpots = (spots) => {
  const seen = new Set();
  return spots.filter(s => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
};

export default function MapPage() {
  const { user, isAuthenticated, isAdmin, isModerator, logout, loading: authLoading } = useAuth();
  const [spots, setSpots] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isCruisingOpen, setIsCruisingOpen] = useState(false);
  const [isTracesOpen, setIsTracesOpen] = useState(false);
  const [isAddingSpot, setIsAddingSpot] = useState(false);
  const [isAddWarningOpen, setIsAddWarningOpen] = useState(false);
  const [isAdminSpotWarningOpen, setIsAdminSpotWarningOpen] = useState(false);
  const [isLocationNotAllowedOpen, setIsLocationNotAllowedOpen] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [spotTraces, setSpotTraces] = useState(null);
  const [myTrace, setMyTrace] = useState(null);
  
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]);
  const [userPos, setUserPos] = useState(null);
  const watchIdRef = useRef(null);

  const [isNearbyWarningOpen, setIsNearbyWarningOpen] = useState(false);
  const [nearbySpots, setNearbySpots] = useState([]);
  const [pendingLatLng, setPendingLatLng] = useState(null);
  const [moderationWarning, setModerationWarning] = useState(null);
  const [photoCheckWarning, setPhotoCheckWarning] = useState(null);
  const [photoUploadWarning, setPhotoUploadWarning] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [inAppDone, setInAppDone] = useState(false);
  const descriptionRef = useRef(null);
  const fileInputGalleryRef = useRef(null);
  const fileInputCameraRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "", description: "", category: "facile", tags: [], lat: 0, lng: 0,
  });
  
const [filters, setFilters] = useState({
  category: "",
  tags: [],
  search: "",
  showMasked: true, // utilisé uniquement pour l'admin
});
  
  const [reportData, setReportData] = useState({ reason: "", comment: "" });

  const loadSpots = useCallback(async () => {
    try {
// Public: visibles uniquement / Admin: tout (visible + masked + draft)
const data = await api.getSpots(
  isAdmin
    ? { ...filters, includeAll: true, applyFuzz: false }
    : filters
);
setSpots(deduplicateSpots(data));
    } catch (error) {
      console.error("Failed to load spots:", error);
    }
}, [filters, isAdmin]);

  useEffect(() => {
    api.getConfig().then(setConfig).catch(console.error);
    loadSpots();
  }, [loadSpots]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    // Centrage initial une seule fois
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setMapCenter([coords.latitude, coords.longitude]);
        setUserPos([coords.latitude, coords.longitude]);
      },
      () => {} // refus ou erreur → fallback Paris inchangé
    );
    // Suivi temps réel pour le point bleu uniquement
    watchIdRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => setUserPos([coords.latitude, coords.longitude]),
      () => {}
    );
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (!authLoading && user && user.onboarding_done !== true) {
      setShowOnboarding(true);
    }
  }, [user, authLoading]);

  const loadSpotTraces = async (spotId) => {
    try {
      const traces = await api.getSpotTraces(spotId);
      setSpotTraces(traces);
      if (isAuthenticated) {
        const myTraceData = await api.getMyTrace(spotId);
        setMyTrace(myTraceData);
      }
    } catch (error) {
      console.error("Failed to load traces:", error);
    }
  };

  const handleMapClick = async (latlng) => {
    if (!isAuthenticated) {
      toast.error("Connectez-vous pour créer un spot");
      setIsAuthOpen(true);
      return;
    }
    setIsAddingSpot(false);
    // Vérification géographique (fail-safe : erreur réseau → on laisse passer)
    try {
      const check = await api.checkSpotLocation(latlng.lat, latlng.lng);
      if (check.allowed === false) {
        setIsLocationNotAllowedOpen(true);
        return;
      }
    } catch (_) {}

    try {
      const nearby = await api.getNearbySpots(latlng.lat, latlng.lng);
      if (nearby.length > 0) {
        setNearbySpots(nearby);
        setPendingLatLng(latlng);
        setIsNearbyWarningOpen(true);
        return;
      }
    } catch (_) {
      // En cas d'erreur réseau on continue normalement
    }
    setFormData(prev => ({ ...prev, lat: latlng.lat, lng: latlng.lng }));
    setIsFormOpen(true);
  };

  const handleMarkerClick = (spot) => {
    setSelectedSpot(spot);
    setIsDetailOpen(true);
    setSpotTraces(null);
    setMyTrace(null);
    loadSpotTraces(spot.id);
  };

  const hasOpenedFromUrl = useRef(false);
  const hasPromptedLoginFromUrl = useRef(false);

  const openSpotByIdFromCruising = useCallback(async (spotId) => {
    if (!isAuthenticated) {
      toast.error("Connecte-toi pour voir les détails");
      setIsAuthOpen(true);
      return;
    }
    try {
      const spot = await api.getSpot(spotId);
      handleMarkerClick(spot);
      setIsCruisingOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'ouvrir ce spot");
    }
  }, [handleMarkerClick, isAuthenticated]);

  const { id: spotIdFromUrl } = useParams();
  useEffect(() => {
    if (!spotIdFromUrl || hasOpenedFromUrl.current || authLoading) return;
    if (!isAuthenticated) {
      // Non connecté : toast + modal de login une seule fois, sans poser le verrou d'ouverture
      if (hasPromptedLoginFromUrl.current) return;
      hasPromptedLoginFromUrl.current = true;
      openSpotByIdFromCruising(spotIdFromUrl);
      return;
    }
    hasOpenedFromUrl.current = true;
    openSpotByIdFromCruising(spotIdFromUrl);
  }, [spotIdFromUrl, openSpotByIdFromCruising, authLoading, isAuthenticated]);

  const resetForm = () => {
    setFormData({ title: "", description: "", category: "facile", tags: [], status: "public", lat: 0, lng: 0 });
    setSelectedPhotos([]);
    setEditingSpot(null);
    setPhotoCheckWarning(null);
  };

  const handleSelectPhotos = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setSelectedPhotos(prev => {
      if (prev.length >= 2) {
        toast.error("Vous avez déjà sélectionné 2 photos");
        return prev;
      }
      const merged = [...prev, ...newFiles];
      if (merged.length > 2) toast.error("Maximum 2 photos");
      return merged.slice(0, 2);
    });
    e.target.value = "";
  };

  const handleSaveSpot = async (forcePublish = false, skipPhotoCheck = false) => {
    if (!isAuthenticated) {
      toast.error("Connectez-vous pour continuer");
      setIsAuthOpen(true);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Pré-vérification des photos avant création du spot
      if (!editingSpot && !skipPhotoCheck && selectedPhotos.length > 0) {
        for (const file of selectedPhotos) {
          const check = await api.checkPhoto(file);
          if (check.approved === false && !check.unchecked) {
            setPhotoCheckWarning({ reason: check.reason });
            return;
          }
        }
      }
      setPhotoCheckWarning(null);

      const spotData = { ...formData, force_publish: forcePublish };

      let spotId = editingSpot?.id;

      if (editingSpot) {
        await api.updateSpot(editingSpot.id, spotData);
        toast.success("Spot modifié");
      } else {
        const created = await api.createSpot(spotData);
        spotId = created?.id;
        toast.success("Nouveau spot créé");
      }

      // Upload des photos sélectionnées (création seulement)
      if (!editingSpot && spotId && selectedPhotos.length > 0) {
        try {
          for (const file of selectedPhotos) {
            await api.uploadPhoto(spotId, file);
          }
        } catch (uploadError) {
          const status = uploadError.response?.status;
          const detail = uploadError.response?.data?.detail || "";
          if (status === 413 || (status === 400 && (detail.includes("volumineux") || detail.includes("trop")))) {
            toast.error("Fichier trop volumineux (limite : 10 Mo)");
          } else {
            toast.error(typeof detail === "string" && detail ? detail : uploadError.message || "Erreur upload");
          }
          setIsFormOpen(false);
          setModerationWarning(null);
          resetForm();
          loadSpots();
          return;
        }
      }

      setIsFormOpen(false);
      setModerationWarning(null);
      resetForm();
      loadSpots();
    } catch (error) {
      if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (detail?.detail === "moderation_rejected") {
          setModerationWarning({ reason: detail.reason });
          return;
        }
      }
      if (error.response?.status === 413) {
        toast.error("Fichier trop volumineux (limite : 10 Mo)");
        return;
      }
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : error.message || "Erreur inattendue");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublishSpot = async (spotId) => {
    try {
      await api.publishSpot(spotId);
      toast.success("Spot publié !");
      setIsDetailOpen(false);
      setSelectedSpot(null);
      loadSpots();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur";
      toast.error(msg);
    }
  };

  const handleDeleteSpot = async (spotId) => {
    if (!window.confirm("Supprimer ce spot ?")) return;
    try {
      await api.deleteSpot(spotId);
      toast.success("Spot supprimé");
      setIsDetailOpen(false);
      setSelectedSpot(null);
      loadSpots();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur";
      toast.error(msg);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSpot) return;
    setUploadingPhoto(true);
    try {
      const check = await api.checkPhoto(file);
      if (check.approved === false && !check.unchecked) {
        setPhotoUploadWarning({ file, reason: check.reason });
        return;
      }
      await api.uploadPhoto(selectedSpot.id, file);
      toast.success("Photo ajoutée !");
      const updatedSpots = await api.getSpots(filters);
      setSpots(deduplicateSpots(updatedSpots));
      const updated = updatedSpots.find(s => s.id === selectedSpot.id);
      if (updated) setSelectedSpot(updated);
    } catch (error) {
      if (error.response?.status === 413) {
        toast.error("Fichier trop volumineux (limite : 10 Mo)");
        return;
      }
      const msg = error.response?.data?.detail || "Erreur upload";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleForcePhotoUpload = async () => {
    if (!photoUploadWarning) return;
    const file = photoUploadWarning.file;
    setPhotoUploadWarning(null);
    setUploadingPhoto(true);
    try {
      await api.uploadPhoto(selectedSpot.id, file);
      toast.success("Photo ajoutée !");
      const updatedSpots = await api.getSpots(filters);
      setSpots(deduplicateSpots(updatedSpots));
      const updated = updatedSpots.find(s => s.id === selectedSpot.id);
      if (updated) setSelectedSpot(updated);
    } catch (error) {
      if (error.response?.status === 413) {
        toast.error("Fichier trop volumineux (limite : 10 Mo)");
        return;
      }
      const msg = error.response?.data?.detail || "Erreur upload";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoUrl) => {
    if (!selectedSpot) return;
    const filename = photoUrl.split("/").pop();
    try {
      await api.deletePhoto(selectedSpot.id, filename);
      toast.success("Photo supprimée");
      const updatedSpots = await api.getSpots(filters);
      setSpots(deduplicateSpots(updatedSpots));
      const updated = updatedSpots.find(s => s.id === selectedSpot.id);
      if (updated) setSelectedSpot(updated);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleAdminQuickEdit = async (field, value) => {
    if (!selectedSpot) return;
    try {
      const updated = await api.updateSpot(selectedSpot.id, { [field]: value });
      setSelectedSpot(updated);
      loadSpots();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleTrace = async (traceType) => {
    if (!isAuthenticated) {
      toast.error("Connectez-vous pour laisser une trace");
      setIsAuthOpen(true);
      return;
    }
    if (!selectedSpot) return;
    try {
      if (myTrace?.trace_type === traceType) {
        await api.deleteTrace(selectedSpot.id);
        setMyTrace(null);
        toast.success("Trace supprimée");
      } else {
        await api.createOrUpdateTrace(selectedSpot.id, traceType);
        toast.success("Trace enregistrée !");
      }
      loadSpotTraces(selectedSpot.id);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleReport = async () => {
    if (!selectedSpot || !reportData.reason) return;
    try {
      await api.createReport({ spot_id: selectedSpot.id, reason: reportData.reason, comment: reportData.comment || null });
      toast.success("Signalement envoyé");
      setIsReportOpen(false);
      setReportData({ reason: "", comment: "" });
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const startEditing = (spot) => {
    if (!canEditSpot(spot)) {
      toast.error("Vous ne pouvez pas modifier ce spot");
      return;
    }
    setEditingSpot(spot);
    setFormData({
      title: spot.title, 
      description: (spot.description || "").slice(0, DESCRIPTION_MAX),
      category: spot.category,
      tags: spot.tags || [],
      status: spot.status, 
      lat: spot.lat, 
      lng: spot.lng,
    });
    setIsDetailOpen(false);
    setIsFormOpen(true);
  };

  const canEditSpot = (spot) => {
    if (!isAuthenticated || !spot) return false;
    if (isAdmin) return true;
    if (!spot.creator_user_id) return false;
    return spot.creator_user_id === user?.id;
  };

  const handleAddSpotClick = () => {
    if (!isAuthenticated) {
      toast.error("Connectez-vous pour ajouter un spot");
      setIsAuthOpen(true);
      return;
  }

  // Si on est déjà en mode ajout, on annule sans avertissement
  if (isAddingSpot) {
    setIsAddingSpot(false);
    return;
  }

  // Admin : avertissement spécifique avant le warning standard
  if (isAdmin) {
    setIsAdminSpotWarningOpen(true);
    return;
  }

  // Sinon : on affiche l'avertissement (à chaque fois)
  setIsAddWarningOpen(true);
};

  const handleLockedMarkerClick = () => {
    toast.error("Connecte-toi pour voir les détails");
    setIsAuthOpen(true);
  };

  // Render markers
const renderMarkers = () => {
  const markers = [];
  for (let i = 0; i < spots.length; i++) {
    const spot = spots[i];
    const status = spot.status || "visible";

    // Ne jamais afficher les drafts sur la carte (même admin) pour garder la carte clean
    if (status === "draft") continue;

    // Les masqués : admin only + toggle "Afficher masqués"
    if (status === "masked") {
      if (!isAdmin) continue;
      if (!filters.showMasked) continue;

      markers.push(
        <Marker
          key={spot.id}
          position={[spot.lat, spot.lng]}
          icon={createMarkerIcon(spot.category, { hidden: true })}
          eventHandlers={{ click: () => (isAuthenticated ? handleMarkerClick(spot) : handleLockedMarkerClick()) }}
        />
      );
      continue;
    }

    // Visible (public)
    markers.push(
      <Marker
        key={spot.id}
        position={[spot.lat, spot.lng]}
        icon={createMarkerIcon(spot.category)}
        eventHandlers={{ click: () => (isAuthenticated ? handleMarkerClick(spot) : handleLockedMarkerClick()) }}
        />
    );
  }
  return markers;
};

  // Render trace buttons
  const renderTraceButtons = () => {
    const types = ["passed", "regular", "want_to_try"];
    const buttons = [];
    for (let i = 0; i < types.length; i++) {
      const type = types[i];
      const isSelected = myTrace?.trace_type === type;
      buttons.push(
        <Button
          key={type}
          variant="outline"
          size="sm"
          className={
            "text-xs border-white/15 text-white/80 hover:bg-white/10 " +
            (isSelected ? "bg-white/10" : "bg-white/5")
          }
          onClick={() => handleTrace(type)}
          data-testid={"trace-" + type + "-button"}
        >
          {type === "passed" && <span className="mr-1">🗺️</span>}
          {type === "regular" && <span className="mr-1">🔥</span>}
          {type === "want_to_try" && <span className="mr-1">🫦</span>}
          {TRACE_LABELS[type]}
        </Button>
      );
    }
    return buttons;
  };

  const TRACE_GROUPS = [
    { type: "passed",      emoji: "🗺️", label: "J'y suis passé" },
    { type: "regular",     emoji: "🔥", label: "Régulier"        },
    { type: "want_to_try", emoji: "🫦", label: "Envie"           },
  ];

  const renderTracesUsers = () => {
    if (!spotTraces?.users?.length) {
      return <p className="text-center text-white/60 py-4">Aucune trace</p>;
    }
    const byType = {};
    for (const u of spotTraces.users) {
      if (!byType[u.trace_type]) byType[u.trace_type] = [];
      byType[u.trace_type].push(u.pseudo);
    }
    const groups = TRACE_GROUPS.filter(g => byType[g.type]?.length);
    return (
      <div className="space-y-4 max-h-60 overflow-y-auto">
        {groups.map(g => (
          <div key={g.type}>
            <p className="text-sm font-semibold text-white mb-1">{g.emoji} {g.label}</p>
            <p className="text-sm text-white/70">{byType[g.type].join(" · ")}</p>
          </div>
        ))}
      </div>
    );
  };

  const photosCount = selectedSpot?.photos?.length || 0;
  const canUpload = photosCount < 2 && isAuthenticated && canEditSpot(selectedSpot);
  const isFormValid = formData.title && formData.lat && formData.lng;

    // === UrbSex UI: Dark spot detail panel + subtle category tint ===
  const CATEGORY_TINT_HEX = {
    facile: "#34C759",
    audacieux: "#FFD60A",
    temporaire: "#FF3B30",
  };

  const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== "string") return "rgba(0,0,0,0)";
    const h = hex.replace("#", "");
    if (h.length !== 6) return "rgba(0,0,0,0)";
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const spotTintHex = selectedSpot?.category ? CATEGORY_TINT_HEX[selectedSpot.category] : null;
  const spotTintA = hexToRgba(spotTintHex, 0.08);
  const spotTintB = hexToRgba(spotTintHex, 0.05);

  const spotDetailSheetStyle = spotTintHex
    ? {
        backgroundColor: "rgba(9, 9, 11, 0.96)", // zinc-950-ish
        backgroundImage: `radial-gradient(900px circle at 0% 0%, ${spotTintA}, transparent 55%),
                          radial-gradient(700px circle at 100% 0%, ${spotTintB}, transparent 45%)`,
      }
    : { backgroundColor: "rgba(9, 9, 11, 0.96)" };

  return (
    <div className="relative h-full w-full" data-testid="map-page">
      <AppHeader
        user={user}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        isModerator={isModerator}
        logout={logout}
        onLoginClick={() => setIsAuthOpen(true)}
        onAccountClick={() => setIsAccountOpen(true)}
        onCruisingClick={() => setIsCruisingOpen(true)}
      />

      <CruisingSheet
        open={isCruisingOpen}
        onOpenChange={setIsCruisingOpen}
        onOpenSpotById={openSpotByIdFromCruising}
      />

      <AccountSheet
        open={isAccountOpen}
        onOpenChange={setIsAccountOpen}
        onLogoutAfterDelete={logout}
        isAuthenticated={isAuthenticated}
      />

      <MapContainer center={mapCenter} zoom={13} className="h-full w-full" zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCenter center={mapCenter} focusTarget={focusTarget} />
        <MapClickHandler onMapClick={handleMapClick} isAddingSpot={isAddingSpot} />
        {userPos && (
          <CircleMarker
            center={userPos}
            radius={8}
            pathOptions={{ color: "#1d6ef5", fillColor: "#4a90e2", fillOpacity: 0.9, weight: 2 }}
          />
        )}
        {renderMarkers()}
      </MapContainer>

        <div className="absolute top-24 sm:top-16 left-4 right-4 z-40 flex gap-2">
                    <Button data-testid="filter-button" variant="secondary" size="sm" className="rounded-full bg-zinc-950/80 text-white border border-white/10 hover:bg-zinc-900/80 backdrop-blur-md" onClick={() => setIsFilterOpen(true)}>
            <Filter className="w-4 h-4 mr-1" />Filtres
          </Button>
          {filters.category && (
            <Badge className="rounded-full cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, category: "" }))}>
              {filters.category} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
        </div>

      {/* Bottom Actions */}
      <div className="absolute bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 z-40 flex justify-center md:justify-end gap-3">
        <Button
          data-testid="add-spot-button"
          className={isAddingSpot ? "rounded-full h-14 px-6 bg-amber-500 hover:bg-amber-600" : "rounded-full h-14 px-6 bg-slate-900 hover:bg-slate-800"}
          onClick={handleAddSpotClick}
        >
          {isAddingSpot ? <><X className="w-5 h-5 mr-2" />Annuler</> : <><Plus className="w-5 h-5 mr-2" />Ajouter un spot</>}
        </Button>
        {isAdmin && (
          <Button variant="outline" size="icon" className="glass rounded-full w-12 h-12" onClick={() => window.location.href = "/admin"}>
            <Menu className="w-5 h-5" />
          </Button>
        )}
      </div>

      {isAddingSpot && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 glass rounded-full px-6 py-3 shadow-lg">
          <span className="text-sm font-medium text-slate-700">Cliquez sur la carte</span>
        </div>
      )}

      {/* Filter Sheet */}
      <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <SheetContent
          side="left"
          className="w-80 bg-[#0f0d0e] text-white border-white/10"
        >
          <SheetHeader>
            <SheetTitle className="heading text-2xl text-white">Filtres</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={filters.category || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, category: v === "all" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Toutes" /></SelectTrigger>
                <SelectContent className="bg-[#0f0d0e] text-white border-white/10">
                  <SelectItem className="focus:bg-white/10 focus:text-white" value="all">Toutes</SelectItem>
                  <SelectItem className="focus:bg-white/10 focus:text-white" value="facile">Facile</SelectItem>
                  <SelectItem className="focus:bg-white/10 focus:text-white" value="audacieux">Audacieux</SelectItem>
                  <SelectItem className="focus:bg-white/10 focus:text-white" value="temporaire">Temporaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
  <div className="flex items-center justify-between">
    <Label>Afficher les spots masqués</Label>
    <Checkbox
      checked={!!filters.showMasked}
      onCheckedChange={(v) =>
        setFilters(prev => ({ ...prev, showMasked: !!v }))
      }
    />
  </div>
)}
            <div className="space-y-3">
              <Label>Tags</Label>
              {PREDEFINED_TAGS.map(({ label, tags }) => (
                <div key={label}>
                  <p className="text-xs text-white/40 mb-1">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => {
                      const selected = filters.tags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => setFilters(prev => ({
                            ...prev,
                            tags: selected ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
                          }))}
                          className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                            selected
                              ? "border-white/60 bg-white/15 text-white"
                              : "border-white/20 bg-transparent text-white/50 hover:bg-white/5 hover:text-white/80"
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

<div className="flex gap-2 pt-4">
  <Button
    variant="outline"
    className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/10"
    onClick={() =>
      setFilters(prev => ({
        ...prev,
        category: "",
        tags: [],
        search: "",
        showMasked: true,
      }))
    }
  >
    Reset
  </Button>

  <Button
    className="flex-1 bg-white text-black hover:bg-white/90"
    onClick={() => { loadSpots(); setIsFilterOpen(false); }}
  >
    Appliquer
  </Button>
</div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Spot Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>

        <SheetContent
          className="w-full sm:w-96 overflow-y-auto text-white border-l border-white/10 backdrop-blur-xl"
          style={spotDetailSheetStyle}
        >
          {selectedSpot && (
            <>
              <SheetHeader>
                <SheetTitle className="heading text-2xl pr-8 text-white">{selectedSpot.title}</SheetTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full border-white/20 bg-white/5 text-white hover:bg-white/10"
                  onClick={() => { setFocusTarget({ lat: selectedSpot.lat, lng: selectedSpot.lng }); setIsDetailOpen(false); }}
                >
                  <MapPin className="w-4 h-4 mr-2" />Voir sur la carte
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full border-white/20 bg-white/5 text-white hover:bg-white/10"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/spot/${selectedSpot.id}`);
                    toast.success("Lien copié !");
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" />Partager ce spot
                </Button>
                <div className="flex items-center gap-2 flex-wrap">
{isAdmin ? (
  <Select value={selectedSpot.category} onValueChange={(v) => handleAdminQuickEdit("category", v)}>
    <SelectTrigger className="h-auto py-0.5 px-2 text-xs border-white/20 bg-white/5 text-white w-auto min-w-0">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[#0f0d0e] text-white border-white/10">
      <SelectItem value="facile" className="focus:bg-white/10 focus:text-white">Facile</SelectItem>
      <SelectItem value="audacieux" className="focus:bg-white/10 focus:text-white">Audacieux</SelectItem>
      <SelectItem value="temporaire" className="focus:bg-white/10 focus:text-white">Temporaire</SelectItem>
    </SelectContent>
  </Select>
) : (
  <Badge variant="outline" className={`text-xs capitalize ${CATEGORY_BADGE_CLASS[selectedSpot?.category] || "border-white/20 bg-white/5 text-white/80"}`}>
    {selectedSpot.category}
  </Badge>
)}
                  {selectedSpot.status === "draft" && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid="draft-badge">
                      <FileEdit className="w-3 h-3 mr-1" />Brouillon
                    </Badge>
                  )}
                  {selectedSpot.status === "masked" && (
                    <Badge variant="destructive" data-testid="masked-badge">Masqué</Badge>
                  )}
                {selectedSpot.creator_pseudo && <span className="text-xs text-white/60">par {selectedSpot.creator_pseudo}</span>}
                </div>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-200px)] mt-4">
                <div className="space-y-6 pr-4">
                  {/* Traces */}
                  <div className="space-y-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                    <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsTracesOpen(true)}>
                      <Label className="cursor-pointer">Traces ({spotTraces?.total || 0})</Label>
                      <Button variant="ghost" size="sm">Voir</Button>
                    </div>
                    {spotTraces && spotTraces.total > 0 && (
                      <div className="flex gap-3 text-xs text-white/70">
                        <span>{spotTraces.passed} passés</span>
                        <span>{spotTraces.regular} réguliers</span>
                        <span>{spotTraces.want_to_try} envies</span>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">{renderTraceButtons()}</div>
                  </div>

                  {/* Photos */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Photos ({photosCount}/2)</Label>
                      {canUpload && (
                        <div className="flex gap-2">
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                            <Button variant="outline" size="sm" asChild disabled={uploadingPhoto}>
                              <span>{uploadingPhoto ? "..." : "📂 Fichier"}</span>
                            </Button>
                          </label>
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                            <Button variant="outline" size="sm" asChild disabled={uploadingPhoto}>
                              <span>{uploadingPhoto ? "..." : "📷 Photo"}</span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                    <PhotoGrid photos={selectedSpot.photos} spotTitle={selectedSpot.title} onDelete={canEditSpot(selectedSpot) ? handlePhotoDelete : null} />
                  </div>

                  {selectedSpot.description && (
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <p className="text-sm text-white/80">{selectedSpot.description}</p>
                    </div>
                  )}

                  {(isAdmin || selectedSpot.tags?.length > 0) && (
                    <div className="space-y-2">
                      <Label>Tags</Label>
                      {isAdmin ? (
                        <div className="space-y-2">
                          {PREDEFINED_TAGS.map(({ label, tags }) => (
                            <div key={label}>
                              <p className="text-xs text-white/40 mb-1">{label}</p>
                              <div className="flex flex-wrap gap-2">
                                {tags.map(tag => {
                                  const selected = (selectedSpot.tags || []).includes(tag);
                                  return (
                                    <button
                                      key={tag}
                                      type="button"
                                      onClick={() => {
                                        const current = selectedSpot.tags || [];
                                        const newTags = selected ? current.filter(t => t !== tag) : [...current, tag];
                                        handleAdminQuickEdit("tags", newTags);
                                      }}
                                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                                        selected
                                          ? "border-white/60 bg-white/15 text-white"
                                          : "border-white/20 bg-transparent text-white/50 hover:bg-white/5 hover:text-white/80"
                                      }`}
                                    >
                                      {tag}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <TagList tags={selectedSpot.tags} />
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Coordonnées</Label>
                    <p className="text-xs text-white/55 font-mono">{selectedSpot.lat.toFixed(5)}, {selectedSpot.lng.toFixed(5)}</p>
                  </div>

                  <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                    {/* Bouton Publier pour les brouillons */}
                    {selectedSpot.status === "draft" && canEditSpot(selectedSpot) && (
                      <Button 
                        data-testid="publish-spot-button"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handlePublishSpot(selectedSpot.id)}
                      >
                        <Send className="w-4 h-4 mr-2" />Publier
                      </Button>
                    )}
                    {canEditSpot(selectedSpot) && (
                      <Button variant="outline" onClick={() => startEditing(selectedSpot)} data-testid="edit-spot-button">Modifier</Button>
                    )}
                    {selectedSpot.status === "visible" && (
                      <Button variant="outline" className="text-amber-600" onClick={() => setIsReportOpen(true)}>
                        <AlertTriangle className="w-4 h-4 mr-2" />Signaler
                      </Button>
                    )}
                    {canEditSpot(selectedSpot) && (
                      <Button variant="destructive" onClick={() => handleDeleteSpot(selectedSpot.id)} data-testid="delete-spot-button">Supprimer</Button>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Traces Dialog */}
      <Dialog open={isTracesOpen} onOpenChange={setIsTracesOpen}>
        <DialogContent className="max-w-sm bg-[#0f0d0e] text-white border-white/10">
          <DialogHeader><DialogTitle className="heading">Traces</DialogTitle></DialogHeader>
          <div className="mt-4">{renderTracesUsers()}</div>
        </DialogContent>
      </Dialog>

      {/* Location Not Allowed Dialog */}
      <Dialog open={isLocationNotAllowedOpen} onOpenChange={setIsLocationNotAllowedOpen}>
        <DialogContent className="max-w-md bg-[#0f0d0e] text-white border-white/10">
          <DialogHeader>
            <DialogTitle className="heading text-xl flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-400" />
              Création de spot impossible dans cette zone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-white/80 leading-relaxed mt-2">
            <p>
              UrbSex est disponible uniquement dans les pays disposant de protections
              légales contre la discrimination basée sur l'orientation sexuelle.
            </p>
            <p>
              Cette restriction a pour seul objectif de protéger les utilisateurs
              dans les zones où la visibilité LGBT+ peut représenter un risque réel
              pour leur sécurité.
            </p>
            <p>Notre liste de territoires autorisés est établie sur deux critères objectifs :</p>
            <ul className="list-none space-y-1 pl-2">
              <li>• En Europe : score Rainbow Map ILGA-Europe ≥ 25% (2025)</li>
              <li>• Hors Europe : loi anti-discrimination en droit du travail (ILGA World)</li>
            </ul>
            <a
              href="https://ilga.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-amber-400 hover:underline text-sm"
            >
              En savoir plus → ilga.org
            </a>
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10" onClick={() => setIsLocationNotAllowedOpen(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

{/* Admin Spot Warning Dialog */}
<Dialog open={isAdminSpotWarningOpen} onOpenChange={setIsAdminSpotWarningOpen}>
  <DialogContent className="max-w-sm">
    <DialogHeader>
      <DialogTitle className="heading text-xl flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        Attention
      </DialogTitle>
    </DialogHeader>
    <p className="text-sm leading-relaxed mt-2">
      Le compte administrateur n'est pas destiné à créer des spots.
    </p>
    <div className="flex justify-end gap-2 mt-4">
      <Button variant="outline" onClick={() => setIsAdminSpotWarningOpen(false)}>
        Annuler
      </Button>
      <Button
        className="bg-amber-500 hover:bg-amber-600 text-black"
        onClick={() => {
          setIsAdminSpotWarningOpen(false);
          setIsAddWarningOpen(true);
        }}
      >
        Continuer
      </Button>
    </div>
  </DialogContent>
</Dialog>

{/* Add Spot Warning Dialog */}
<Dialog open={isAddWarningOpen} onOpenChange={setIsAddWarningOpen}>
  <DialogContent className="max-w-md">
    <DialogHeader>
      <DialogTitle className="heading text-xl flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        Avant d’ajouter un spot
      </DialogTitle>
    </DialogHeader>

    <div className="space-y-3 text-sm leading-relaxed mt-2">
      <p className="font-medium">
        Tu ajoutes un spot, pas une story.
      </p>
      <p>
        Photos et description = le lieu uniquement.<br />
        Pas d’infos perso, pas de visages, pas de personnes ou de détails qui identifient quelqu’un.
      </p>

      <div className="space-y-1">
        <p className="font-medium">On reste clean :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Pas de codes d’accès</li>
          <li>Pas d’indications pour forcer une porte</li>
          <li>Pas d’effraction, pas d’incitation à faire n’importe quoi</li>
          <li>Respecte la loi, respecte les gens, respecte le lieu</li>
        </ul>
      </div>

      <p className="font-medium">
        On partage des spots. Pas des galères.
      </p>
      <p className="font-medium">
        Plus c’est propre, plus la communauté reste libre 🤝
      </p>
    </div>

    <div className="flex justify-end gap-2 mt-4">
<Button
  variant="outline"
  onClick={() => setIsAddWarningOpen(false)}
>
  Annuler
</Button>

      <Button
        onClick={() => {
          setIsAddWarningOpen(false);
          setIsAddingSpot(true);
        }}
        className="bg-amber-500 hover:bg-amber-600 text-black"
        data-testid="add-spot-warning-confirm"
      >
        J’ai bien compris
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* Dialog anti-doublon 250m */}
      <Dialog open={isNearbyWarningOpen} onOpenChange={setIsNearbyWarningOpen}>
        <DialogContent className="max-w-sm bg-[#0f0d0e] text-white border-white/10">
          <DialogHeader>
            <DialogTitle className="heading text-xl text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Spot déjà proche
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/70 mt-2">
            Il existe déjà {nearbySpots.length === 1 ? "un spot" : `${nearbySpots.length} spots`} dans un rayon de 250m.
          </p>
          <p className="text-sm text-white/70">
            Es-tu sûr de ne pas créer un doublon ?
          </p>
          <ul className="mt-2 space-y-1">
            {nearbySpots.map(s => (
              <li key={s.id} className="text-sm text-white/90 flex justify-between">
                <span className="truncate">{s.title}</span>
                <span className="ml-2 text-white/50 shrink-0">{s.distance_m} m</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsNearbyWarningOpen(false)}>Annuler</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => {
                setIsNearbyWarningOpen(false);
                setFormData(prev => ({ ...prev, lat: pendingLatLng.lat, lng: pendingLatLng.lng }));
                setIsFormOpen(true);
              }}
            >
              Créer quand même
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={(open) => { if (!open) { setIsFormOpen(false); setModerationWarning(null); resetForm(); } else setIsFormOpen(open); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[#0f0d0e] text-white border-white/10">          

<DialogHeader>
  <DialogTitle className="heading text-2xl text-white">
    {editingSpot ? "Modifier" : "Nouveau spot"}
  </DialogTitle>
</DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                ref={descriptionRef}
                value={formData.description}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData(prev => ({ ...prev, description: v.slice(0, DESCRIPTION_MAX) }));
                }}
                rows={3}
                maxLength={DESCRIPTION_MAX}
              />
              <div className="flex justify-end text-xs text-white/60">
                {formData.description.length}/{DESCRIPTION_MAX}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Catégorie *</Label>

              <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0d0e] text-white border-white/10">
                    <SelectItem className="focus:bg-white/10 focus:text-white" value="facile">Facile</SelectItem>
                    <SelectItem className="focus:bg-white/10 focus:text-white" value="audacieux">Audacieux</SelectItem>
                    <SelectItem className="focus:bg-white/10 focus:text-white" value="temporaire">Temporaire</SelectItem>
                </SelectContent>

              </Select>
            </div>
            <div className="space-y-3">
              <Label>Tags</Label>
              {PREDEFINED_TAGS.map(({ label, tags }) => (
                <div key={label}>
                  <p className="text-xs text-white/40 mb-1">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => {
                      const selected = formData.tags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            tags: selected ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
                          }))}
                          className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                            selected
                              ? "border-white/60 bg-white/15 text-white"
                              : "border-white/20 bg-transparent text-white/50 hover:bg-white/5 hover:text-white/80"
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {/* Info sur le statut brouillon */}

            <div className="p-3 rounded-lg border border-white/10 bg-white/5">
  <p className="text-sm text-white/80">
    <FileEdit className="w-4 h-4 inline mr-1 text-amber-300" />
    {editingSpot ? "Vous pouvez modifier ce spot." : "Vous pouvez ajouter jusqu'à 2 photos."}
  </p>
             </div>


                {!editingSpot && (
            <div className="space-y-2">
              <Label>Photos (0–2)</Label>
              <input type="file" ref={fileInputGalleryRef} accept="image/jpeg,image/png,image/webp,image/*" className="hidden" onChange={handleSelectPhotos} />
              <input type="file" ref={fileInputCameraRef} accept="image/*" capture="environment" className="hidden" onChange={handleSelectPhotos} />
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputGalleryRef.current?.click()}>
                  📂 Choisir un fichier
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputCameraRef.current?.click()}>
                  📷 Appareil photo
                </Button>
              </div>
              {selectedPhotos.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {selectedPhotos.map((file, i) => (
                    <div key={i} className="relative group rounded-lg overflow-hidden aspect-square bg-slate-100">
                      <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPhotos(prev => prev.filter((_, idx) => idx !== i));
                          setPhotoCheckWarning(null);
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
                <p className="text-xs text-white/60">
                 {selectedPhotos.length} / 2 sélectionnée(s)
              </p>
            </div>
                )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input type="number" step="any" value={formData.lat} onChange={(e) => setFormData(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input type="number" step="any" value={formData.lng} onChange={(e) => setFormData(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>

<div className="flex gap-2 pt-4">
  {moderationWarning && (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-3">
      <p className="text-sm text-amber-300">
        ⚠️ Ta description a été signalée : <span className="font-medium">{moderationWarning.reason}</span>
      </p>
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          onClick={() => {
            setModerationWarning(null);
            setTimeout(() => descriptionRef.current?.focus(), 50);
          }}
        >
          Corriger ma description
        </Button>
        <div className="space-y-1">
          <p className="text-xs text-white/50">Ton spot sera publié mais examiné par un modérateur.</p>
          <Button
            size="sm"
            className="w-full bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => handleSaveSpot(true)}
            disabled={isSubmitting}
          >
            Publier quand même
          </Button>
        </div>
      </div>
    </div>
  )}

  {photoCheckWarning && (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-3">
      <p className="text-sm text-amber-300">
        ⚠️ Ta photo a été signalée : <span className="font-medium">{photoCheckWarning.reason}</span>
      </p>
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          onClick={() => setPhotoCheckWarning(null)}
        >
          Corriger ma photo
        </Button>
        <div className="space-y-1">
          <p className="text-xs text-white/50">La photo sera publiée mais examinée par un modérateur.</p>
          <Button
            size="sm"
            className="w-full bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => handleSaveSpot(false, true)}
            disabled={isSubmitting}
          >
            Publier quand même
          </Button>
        </div>
      </div>
    </div>
  )}

  <Button
    variant="outline"
    className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/10"
    onClick={() => { setIsFormOpen(false); resetForm(); }}
  >
    Annuler
  </Button>

  {!moderationWarning && !photoCheckWarning && (
    <Button
      className="flex-1 bg-white text-black hover:bg-white/90"
      onClick={() => handleSaveSpot()}
      disabled={!isFormValid || isSubmitting}
    >
      {isSubmitting ? "Publication..." : editingSpot ? "Enregistrer" : "Créer"}
    </Button>
  )}
</div>

          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="heading">Signaler</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Raison *</Label>
              <Input value={reportData.reason} onChange={(e) => setReportData(prev => ({ ...prev, reason: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Textarea value={reportData.comment} onChange={(e) => setReportData(prev => ({ ...prev, comment: e.target.value }))} rows={3} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { setIsReportOpen(false); setReportData({ reason: "", comment: "" }); }}>Annuler</Button>
              <Button className="flex-1" onClick={handleReport} disabled={!reportData.reason}>Signaler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Warning Dialog (fiche existante) */}
      <Dialog open={!!photoUploadWarning} onOpenChange={(open) => { if (!open) setPhotoUploadWarning(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="heading">Photo signalée</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-3 mt-2">
            <p className="text-sm text-amber-300">
              ⚠️ Ta photo a été signalée : <span className="font-medium">{photoUploadWarning?.reason}</span>
            </p>
            <p className="text-xs text-white/50">La photo sera publiée mais examinée par un modérateur.</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/10"
                onClick={() => setPhotoUploadWarning(null)}
              >
                Corriger ma photo
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                onClick={handleForcePhotoUpload}
              >
                Publier quand même
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <InAppMessageModal isAuthenticated={isAuthenticated} onAllDone={() => setInAppDone(true)} />
      {inAppDone && showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}
      <AuthModal open={isAuthOpen} onOpenChange={setIsAuthOpen} />
    </div>
  );
}
